/**
 * OPEN-BOOK PRICING — the builder's model.
 *
 * Jacky, 15 October 2026, for Joanna (a builder, P-1026-10298): *"I want a transparent model. I will lay
 * out all quote item cost to Artemis FLR. and seek GM of 20%"* … *"I want expand on the per line item and
 * display cost using new line for each item, proper formatted with Quantity, UOM, Per price, sub total.
 * only add 20% GM at bottom total"*.
 *
 * So every line the customer reads is Artemis's COST, carrying its items (quantity × unit cost =
 * subtotal), and the gross margin is applied ONCE, at the total, as 20% of the PRICE (cost ÷ 0.8) — and
 * it is printed, not hidden. This is the one place a cost crosses the customer boundary, and it does so
 * only inside `pricing.open_book`; a standard Proposal never carries `cost_items`
 * (`customer-pricing.test.ts` guards that).
 *
 * The rules are a `bb.config` document (`open_book_pricing`): what will change again — labour rates,
 * wastage, the skirting board prices, the door rule — is data here, not code.
 */
import { z } from 'zod';
import { Offer, defaultSelection, priceSelection } from './customer-selection.js';
import { isCarpetTileProduct } from './room-finish.js';
const rate = z.number().finite().nonnegative().max(1e6);
export const OpenBookRules = z.object({
    /** Gross margin on the PRICE: price = cost ÷ (1 − margin_rate). */
    margin_rate: z.number().min(0).max(0.9),
    /** Installer labour per charged m² — charged on the wastage-inclusive quantity, as the board is. */
    labour_per_m2: rate,
    trims: z.object({
        /** PACO pieces are 3.0 m (Jacky, 15 Oct); the catalogue lists 2.7 / 3.4 m lengths. */
        piece_m: z.number().positive().max(20),
        /** One whole spare piece per trim type (Jacky: "+ 1 whole PC for each type as spare"). */
        spare_pieces: z.number().int().nonnegative().max(10),
        /** Piece cost by the canvas profile name. Fitting is inside the m² labour. */
        piece_costs: z.record(z.string(), rate),
    }),
    stairs: z.object({
        /** $40 per step, glue included (Jacky, 15 Oct). */
        labour_per_step: rate,
        /** "Joanna choose no Riser on this case, so −25% on labor & material (nosing is not reduced)". */
        no_riser_factor: z.number().positive().max(1),
        planks_per_step: z.number().positive().max(10),
        /** Half a nosing length per step when the tread is under 1.2 m. */
        nosing_share: z.number().positive().max(2),
        /** When the catalogue has no plank size: a 1215 × 195 mm laminate plank. */
        plank_m2_default: z.number().positive().max(5),
    }),
    skirting: z.object({
        /** Blacktown Building Supplies Millyard primed pine, 5.4 m lengths, retail INC GST, by nominal height (mm). */
        materials: z.record(z.string(), rate),
        length_m: z.number().positive().max(20),
        gst_divisor: z.number().positive().max(2),
        /** 15% wastage so there are no short joins. */
        wastage: z.number().min(1).max(2),
        /** Installer rate by job size: $5/LM, $3.50/LM once the job is over 100 LM (Jacky, 15 Oct). */
        labour_tiers: z.array(z.object({ over_lm: rate, rate_per_lm: rate })).min(1).max(10),
        /** The height offered when the Proposal does not say. */
        default_size: z.string().max(10),
    }),
    architrave: z.object({
        size: z.string().max(10),
        /** 5 pieces per standard door (4 legs at half a length + 1 length for both heads) = 3 lengths; 6 pieces (4 lengths) when wider than `wide_over_m`. */
        lengths_standard: z.number().int().positive().max(10),
        lengths_wide: z.number().int().positive().max(10),
        wide_over_m: z.number().positive().max(10),
        /** $35 per door, both sides (Jacky, 15 Oct: "install 17 door is = effort of doing 170 LM of skirting"). */
        labour_per_door: rate,
        /** Which plan door kinds take architraves; open doorways never do. */
        include_kinds: z.array(z.string().max(24)).max(10),
        /** An entrance door is wider and arrives fully framed; sliders to the alfresco are wider still. */
        max_door_width_m: z.number().positive().max(10),
    }),
    disposal: z.object({
        /** Offcut disposal when the builder's skip is not available (Jacky: "+$0.5 per SQM offcut disposal"). */
        per_m2: rate,
    }),
});
export const openBookDefaults = {
    margin_rate: 0.2, labour_per_m2: 15,
    trims: { piece_m: 3, spare_pieces: 1, piece_costs: { 'L Trim': 10, 'U Channel': 15 } },
    stairs: { labour_per_step: 40, no_riser_factor: 0.75, planks_per_step: 2, nosing_share: 0.5, plank_m2_default: 1.215 * 0.195 },
    skirting: { materials: { '66': 23.49, '90': 31.86, '140': 48.06 }, length_m: 5.4, gst_divisor: 1.1, wastage: 1.15, labour_tiers: [{ over_lm: 0, rate_per_lm: 5 }, { over_lm: 100, rate_per_lm: 3.5 }], default_size: '66' },
    architrave: { size: '66', lengths_standard: 3, lengths_wide: 4, wide_over_m: 2.6, labour_per_door: 35, include_kinds: ['door', 'hinged', 'sliding', 'cavity'], max_door_width_m: 1.0 },
    disposal: { per_m2: 0.5 },
};
/** The catalogue costs the command reads for this Proposal; nothing here is typed by a person. */
export function skirtingLabourRate(run, rules) {
    return [...rules.skirting.labour_tiers].sort((a, b) => a.over_lm - b.over_lm).reduce((rate, t) => run > t.over_lm ? t.rate_per_lm : rate, rules.skirting.labour_tiers[0].rate_per_lm);
}
export function skirtingMaterialPerLm(size, rules) {
    const length = rules.skirting.materials[size];
    return length === undefined ? null : Math.round(length / rules.skirting.gst_divisor / rules.skirting.length_m * 100) / 100;
}
const polylineM = (points) => points.slice(1).reduce((n, p, i) => n + Math.hypot(p.x - points[i].x, p.y - points[i].y), 0);
const floorKey = (name) => name.trim().toLowerCase();
/**
 * THE OFFER, from the frozen Proposal and the catalogue: the rooms in scope with their polygons, the
 * products Artemis dictates with their costs and colours, the underlay under each floor, the trims the
 * canvas traced, the stairs, the skirting run less the rooms left out, the doors that take architraves,
 * and the rules of the day. `priceSelection` prices any selection on it; the released copy carries the
 * default one (every room on the recommended product, nothing optional on).
 *
 * Floor lines: board + underlay + $15/m² labour on the wastage quantity, the trims inside the hard-floor
 * line ("consumable such as saw blade, and trim, dont put in own line, its sub details in the Laminate").
 * The saw blade is an own-crew consumable and never appears ("customer dont need to know that").
 */
export function buildOffer(doc, costs, rules, options = {}) {
    const blockers = [];
    const plan = doc.assessment.plan;
    const selections = (doc.pricing?.products ?? []).filter(p => p.role !== 'option');
    const recommendedId = selections[0]?.productId ?? costs.products.find(p => p.recommended)?.id ?? costs.products[0]?.id;
    if (!costs.products.length) {
        blockers.push('no products offered');
        return { offer: null, blockers };
    }
    const waste = selections[0]?.quantity_multiplier ?? 1.05;
    // A carpet tile is never roll carpet, whoever built the costs (18 Oct 2026): it rides as an area product with no roll width,
    // so the roll run, the carpet underlay and the bedrooms-carpet preset below all pass it by.
    const products = costs.products.map(p => { const tile = isCarpetTileProduct(p.type, p.sub_type); return { id: p.id, name: p.name, type: tile ? 'hard_floor' : p.type, sub_type: p.sub_type, board: p.board, waste, plank: p.plank, roll_width_m: tile ? null : p.roll_width_m, nosing_price: p.nosing_price, colours: p.colours, recommended: p.id === recommendedId, intro: p.intro ?? '', rooms: null }; });
    if (!products.some(p => p.recommended))
        products[0].recommended = true;
    // Rooms in scope: a finish on the plan, not out of scope, not a void. Polygons are metres, as the canvas holds them.
    const rooms = plan.flatMap(f => f.rooms.filter(r => !!r.finish && !r.out_of_scope && !r.is_void && r.in_proposal !== false && r.shape.length >= 3).map(r => ({ id: r.id, name: r.name, floor: f.name, area_m2: r.area_m2, polygon: r.shape.map(p => ({ x: p.x, y: p.y })) })));
    if (!rooms.length)
        blockers.push('no rooms in scope on the plan');
    // Underlay under each floor, from the accessories bound to the recommended product.
    const underlays = [];
    for (const a of doc.accessories.filter(a => a.kind === 'underlay' && a.on && a.bound_to === recommendedId)) {
        const u = a.product_id ? costs.underlays[a.product_id] : undefined;
        if (!u) {
            blockers.push(`${a.name} — no catalogue cost`);
            continue;
        }
        const floor = plan.find(f => floorKey(f.name) === floorKey(a.area_id.replace(/^drawing:/, '')))?.name ?? a.area_id.replace(/^drawing:/, '');
        underlays.push({ floor, name: u.name, cost: u.cost });
    }
    const trimRuns = new Map();
    for (const f of plan)
        for (const t of f.trims) {
            const profile = t.profile || t.kind || 'Trim';
            trimRuns.set(profile, (trimRuns.get(profile) ?? 0) + (t.length_m ?? polylineM(t.points)));
        }
    const trims = [];
    for (const [profile, run] of trimRuns) {
        const cost = rules.trims.piece_costs[profile];
        if (cost === undefined) {
            blockers.push(`${profile} — no piece cost in open_book_pricing`);
            continue;
        }
        trims.push({ profile, run_m: Math.round(run * 100) / 100, piece_cost: cost });
    }
    const steps = doc.assessment.stairs.reduce((n, s) => n + s.steps, 0);
    const stairService = doc.services.find(s => s.service_id === 'stair_work' || s.service_id === 'carpet_stair_install');
    const skirting = doc.services.find(s => s.service_id === 'skirting' && s.offered);
    const excludedIds = new Set(skirting?.skirting_room_exclusions ?? []), excludedNames = new Set((options.skirting_exclude_rooms ?? []).map(n => n.trim().toLowerCase()));
    const run = Math.round(plan.reduce((n, f) => n + f.skirting.rooms.filter(r => !excludedIds.has(r.area_id) && !excludedNames.has(r.name.trim().toLowerCase())).reduce((m, r) => m + r.skirting_m, 0), 0) * 100) / 100;
    const doors = plan.flatMap(f => f.doors).filter(d => rules.architrave.include_kinds.includes(d.kind)).map(d => Math.hypot(d.to.x - d.from.x, d.to.y - d.from.y)).filter(w => w < rules.architrave.max_door_width_m);
    const doorLengths = doors.reduce((n, w) => n + (w > rules.architrave.wide_over_m ? rules.architrave.lengths_wide : rules.architrave.lengths_standard), 0);
    const archMaterial = skirtingMaterialPerLm(rules.architrave.size, rules);
    const sizes = Object.keys(rules.skirting.materials).sort((a, b) => Number(a) - Number(b)).map(size => ({ size, material_per_lm: skirtingMaterialPerLm(size, rules) }));
    // Presets Jacky dictates in words: everything on the recommended board; and, when a carpet is offered, the bedrooms in it.
    const recommended = products.find(p => p.recommended);
    const presets = [{ id: 'all', name: recommended.name, description: '', rooms: Object.fromEntries(rooms.map(r => [r.id, recommended.id])) }];
    const carpet = products.find(p => p.type === 'carpet');
    // Jacky, 15 Oct: "majority laminate but First floor the 3 Bedroom to have carpet" and "carpet option only 3 bedroom upstair" —
    // a carpet is OFFERED in the bedrooms upstairs (every bedroom when the house has one level) and nowhere else; the preset puts it there.
    if (carpet) {
        const upper = rooms.filter(r => /\b(first|upper|level ?1|1f)\b/i.test(r.floor));
        const pool = upper.length ? upper : rooms;
        const beds = pool.filter(r => /\b(bed|master)/i.test(r.name));
        for (const p of products)
            if (p.type === 'carpet')
                p.rooms = beds.map(b => b.id);
        if (beds.length)
            presets.push({ id: 'bedrooms-carpet', name: `${recommended.name} + ${carpet.name}`, description: beds.map(b => b.name).join(', '), rooms: Object.fromEntries(rooms.map(r => [r.id, beds.includes(r) ? carpet.id : recommended.id])) });
    }
    const offer = Offer.parse({
        products, rooms, underlays, carpet_underlay: costs.carpet_underlay, trims, steps, stair_no_riser: /no riser/i.test(stairService?.name ?? ''),
        skirting_run_lm: skirting ? run : 0, skirting_excluded: (options.skirting_exclude_rooms ?? []).map(n => n.trim()).filter(Boolean), doors: doors.length, door_lengths: doorLengths, profiles: costs.profiles ?? [], presets,
        rules: { margin_rate: options.margin_rate ?? rules.margin_rate, labour_per_m2: rules.labour_per_m2, trims: { piece_m: rules.trims.piece_m, spare_pieces: rules.trims.spare_pieces }, stairs: rules.stairs,
            skirting: { wastage: rules.skirting.wastage, length_m: rules.skirting.length_m, labour_per_lm: skirtingLabourRate(run, rules), sizes },
            architrave: { size: rules.architrave.size, lengths_standard: rules.architrave.lengths_standard, lengths_wide: rules.architrave.lengths_wide, wide_over_m: rules.architrave.wide_over_m, labour_per_door: rules.architrave.labour_per_door, material_per_length: Math.round((archMaterial ?? 0) * rules.skirting.length_m * 100) / 100 },
            disposal: { per_m2: rules.disposal.per_m2 } },
    });
    return { offer: blockers.length ? null : offer, blockers };
}
/** The released copy: the offer and its default selection priced. */
export function openBookPricing(doc, costs, rules, options = {}) {
    const { offer, blockers } = buildOffer(doc, costs, rules, options);
    if (!offer)
        return { pricing: null, offer: null, blockers };
    return { pricing: priceSelection(offer, defaultSelection(offer), options.lang ?? 'en'), offer, blockers };
}
//# sourceMappingURL=open-book-pricing.js.map